import type { DietTypes, Household, KnownAllergyId, User } from '../types';
import { normalizeKnownAllergies } from './allergies';
import { DIET_TYPE_KEYS, normalizeDietTypes } from './dietTypes';
import type { RecipeFilterState } from './recipeFilters';

export type HouseholdFitProfile = {
    excludeAllergens: KnownAllergyId[];
    dietTypes: (keyof DietTypes)[];
};

export function aggregateHouseholdFitProfile(
    household: Household | undefined | null,
    memberUsers: readonly User[] = [],
): HouseholdFitProfile {
    const allergyIds = new Set<KnownAllergyId>();
    const dietKeys = new Set<keyof DietTypes>();

    for (const tenant of household?.tenants ?? []) {
        for (const allergyId of normalizeKnownAllergies(tenant.knownAllergies)) {
            allergyIds.add(allergyId);
        }
        const tenantDiets = normalizeDietTypes(tenant.dietTypes);
        for (const key of DIET_TYPE_KEYS) {
            if (tenantDiets[key]) {
                dietKeys.add(key);
            }
        }
    }

    const memberIds = new Set(household?.members ?? []);
    for (const user of memberUsers) {
        if (!memberIds.has(user.uid)) continue;

        for (const allergyId of normalizeKnownAllergies(user.knownAllergies)) {
            allergyIds.add(allergyId);
        }
        const memberDiets = normalizeDietTypes(user.userDietTypes);
        for (const key of DIET_TYPE_KEYS) {
            if (memberDiets[key]) {
                dietKeys.add(key);
            }
        }
    }

    return {
        excludeAllergens: normalizeKnownAllergies([...allergyIds]),
        dietTypes: DIET_TYPE_KEYS.filter((key) => dietKeys.has(key)),
    };
}

export function hasHouseholdFitProfile(profile: HouseholdFitProfile): boolean {
    return profile.excludeAllergens.length > 0 || profile.dietTypes.length > 0;
}

export function mergeHouseholdFitIntoFilters(
    filters: RecipeFilterState,
    profile: HouseholdFitProfile,
): RecipeFilterState {
    const excludeAllergens = normalizeKnownAllergies([
        ...filters.excludeAllergens,
        ...profile.excludeAllergens,
    ]);
    const dietTypes = DIET_TYPE_KEYS.filter(
        (key) => filters.dietTypes.includes(key) || profile.dietTypes.includes(key),
    );

    return {
        ...filters,
        excludeAllergens,
        dietTypes,
    };
}
